// Lokaler Speicher in IndexedDB. "Abbruch darf nichts verlieren": Zustand,
// Fotos und noch nicht übertragene Aufträge liegen hier, bis der Server sie
// bestätigt hat. Schließt der Kunde den Browser, geht es beim nächsten Öffnen
// des Links genau an derselben Stelle weiter.
//
// Fotos als ArrayBuffer statt Blob: ältere Safari-Versionen hatten Fehler
// beim Speichern von Blobs in IndexedDB.

import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { Draft } from "./zustand.ts";
import type { Op } from "./sync.ts";

type StoredBlob = { photoId: string; token: string; type: string; data: ArrayBuffer };

interface Schema extends DBSchema {
  drafts: { key: string; value: Draft & { savedAt: string } };
  blobs: { key: string; value: StoredBlob; indexes: { token: string } };
  ops: { key: number; value: Op; indexes: { token: string } };
}

export type LocalDb = Awaited<ReturnType<typeof openLocalDb>>;

export async function openLocalDb(name = "umzugsscan-erfassung") {
  const db: IDBPDatabase<Schema> = await openDB<Schema>(name, 1, {
    upgrade(db) {
      db.createObjectStore("drafts", { keyPath: "token" });
      db.createObjectStore("blobs", { keyPath: "photoId" }).createIndex("token", "token");
      db.createObjectStore("ops", { keyPath: "id", autoIncrement: true }).createIndex("token", "token");
    },
  });

  return {
    raw: db,
    getDraft: (token: string) => db.get("drafts", token),
    putDraft: (d: Draft) => db.put("drafts", { ...d, savedAt: new Date().toISOString() }),
    deleteDraft: (token: string) => db.delete("drafts", token),

    async putBlob(token: string, photoId: string, blob: Blob) {
      await db.put("blobs", { photoId, token, type: blob.type || "image/jpeg", data: await blob.arrayBuffer() });
    },
    async getBlob(photoId: string): Promise<Blob | null> {
      const b = await db.get("blobs", photoId);
      return b ? new Blob([b.data], { type: b.type }) : null;
    },
    deleteBlob: (photoId: string) => db.delete("blobs", photoId),
    async deleteBlobsForToken(token: string) {
      const tx = db.transaction("blobs", "readwrite");
      for (const key of await tx.store.index("token").getAllKeys(token)) await tx.store.delete(key);
      await tx.done;
    },

    addOp: (op: Op) => db.add("ops", op) as Promise<number>,
    putOp: (op: Op) => db.put("ops", op),
    deleteOp: (id: number) => db.delete("ops", id),
    /** Aufträge einer Aufnahme in Einfügereihenfolge (autoIncrement-Schlüssel). */
    async listOps(token: string): Promise<Op[]> {
      const ops = await db.getAllFromIndex("ops", "token", token);
      return ops.sort((a, b) => a.id! - b.id!);
    },
    countOps: (token: string) => db.countFromIndex("ops", "token", token),
  };
}

// --------------------------------------------- Zuordnung Link → Aufnahme
// Wer den festen Firmenlink (/b/<slug>) oder seine Einladung (/i/<token>)
// erneut öffnet, soll seine begonnene Aufnahme fortsetzen – auch ohne Netz.
// Kunden-Version: ("k", "privat") → Aufnahme der zuletzt bestätigten E-Mail.

const linkKey = (kind: "b" | "i" | "k", value: string) => `umzugsscan:${kind}:${value}`;

export function rememberLink(kind: "b" | "i" | "k", value: string, token: string) {
  try {
    localStorage.setItem(linkKey(kind, value), token);
  } catch {
    // privater Modus: dann eben ohne
  }
}

export function tokenForLink(kind: "b" | "i" | "k", value: string): string | null {
  try {
    return localStorage.getItem(linkKey(kind, value));
  } catch {
    return null;
  }
}

export function forgetLink(kind: "b" | "i" | "k", value: string) {
  try {
    localStorage.removeItem(linkKey(kind, value));
  } catch {
    // egal
  }
}
